// ============================================================================
// Time & Labor — ONE way to choose a person, everywhere in this module.
//
// ⛔ Ben, 2026-09-22, having tried it himself: "the picker for PUNCH tab of
// the time and Labor module is broken. There is no 'night manager' filter,
// and I still cannot find William."
//
// The cause was not a missing worker. William has always been in the pool —
// and so are FOUR other active Rodriguezes. It was a flat 116-name <select>
// with no typing, where the name alone does not tell you which Rodriguez you
// are looking at even once you have scrolled to him.
//
// So: SmartSelect (TCSD-0171), with the three night managers pinned in a
// labelled group above everybody else, and every row saying who the person is
// rather than only what they are called.
//
// ⛔ TCSD-0163 — PICKERS EXCLUDE FROM THE LIST, NEVER FROM THE FETCH. The
// night-manager group is rendered ABOVE the full list and the same people
// remain in it; nobody is hidden to make the top of the list shorter.
// ============================================================================

import { useMemo } from 'react';
import { SmartSelect, type SmartSelectRow } from '../../components/smart-select';
import type { WorkerPickerRow } from './types';

export interface WorkerPickerProps {
  rows: WorkerPickerRow[];
  value: string | null;
  onChange: (workerId: string) => void;
  /** Shown when nothing is chosen. */
  placeholder?: string;
  /** A picker used as a FILTER offers "everybody"; one used to pick does not. */
  allowClear?: boolean;
  disabled?: boolean;
}

export function WorkerPicker({
  rows,
  value,
  onChange,
  placeholder = 'Type a name…',
  allowClear = false,
  disabled,
}: WorkerPickerProps) {
  // The three night managers, first, by name. They are the people anybody
  // using these screens reaches for most and the hardest to find by surname.
  const nightManagers = useMemo(() => {
    const ms: SmartSelectRow[] = rows
      .filter((r) => r.night_manager_market)
      .sort((a, b) => a.display_name.localeCompare(b.display_name))
      .map((r) => ({
        id: r.worker_id,
        name: r.display_name,
        subtitle: `${r.night_manager_market}${r.has_pin ? '' : ' · no PIN yet'}`,
        raw: r as unknown as Record<string, unknown>,
      }));
    if (!allowClear) return ms;
    // "Everybody" is a row, not a checkbox — a filter that cannot be cleared
    // is a trap, and an empty id is what every caller already means by none.
    return [{ id: '', name: 'Everybody', subtitle: 'No filter', raw: {} }, ...ms];
  }, [rows, allowClear]);

  const groups = nightManagers.length > 0
    ? [{ key: 'night-managers', label: allowClear ? 'Start here' : 'Night managers', rows: nightManagers }]
    : undefined;

  return (
    <SmartSelect
      entity="worker"
      value={value}
      onChange={(id) => onChange(id)}
      placeholder={placeholder}
      entityLabel="worker"
      disabled={disabled}
      extraGroups={groups}
    />
  );
}
